// Reusable Payments panel + Record Payment modal. Used by:
//   - InvoicesTab (deposit / final / reimbursable)
//   - DesignerHoursSection (designer hours invoices)
//   - BillingSummarySection (monthly billing summaries)
//
// Renders an Asked / Paid / Balance summary, a payment-history list with
// per-entry remove buttons, and a "+ Record Payment" trigger that opens the
// shared modal for amount / date / method / reference / notes capture.
//
// Pure presentational — caller wires data and handlers via props. Behavior
// (status flips, retainer auto-draws) lives in the caller. The panel reads
// from the relational client_payments + payment_allocations world via the
// `entries` prop (pre-shaped by paymentEntriesForDocument(...) in utils.js)
// and emits a fully-formed { payment, allocations } pair when the user
// records a new payment via the modal.

import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import { fmtMoney } from '../utils';

var PAYMENT_METHODS = ['', 'Wire', 'Check', 'ACH', 'Zelle', 'Credit card', 'Other'];

// ── PaymentsPanel ─────────────────────────────────────────────────────────
//
// props:
//   amountDue          number — the asked amount on the document
//   entries            array  — pre-shaped from paymentEntriesForDocument(...)
//                               each entry has { id, amount, date, method,
//                               reference, bankReference, notes, recordedBy,
//                               paymentId } where `id` is the allocation id
//   canManage          bool   — gates Record Payment + delete buttons
//                               (the Record button ALSO requires
//                               onRecordPayment to be wired — surfaces that
//                               deliberately omit it, like the invoice
//                               detail since 2026-07-04, get a read-only
//                               history panel; recording lives in Billing
//                               History's package wizard)
//   userEmail          string — stamped on new entries as recordedBy
//   documentContext    object — { documentType, documentId, clientId,
//                                projectLabel } needed to build the
//                                client_payment + allocation pair on submit
//   onRecordPayment    (payment, allocations) => void
//   onRemoveAllocation (allocationId) => void
//   compact            bool   — smaller variant for embedded use (optional)
export default function PaymentsPanel(props) {
  var ctxApp = useAppContext();
  var amountDue = parseFloat(props.amountDue) || 0;
  var entries = props.entries || [];
  var paid = entries.reduce(function(s, e) {
    var n = parseFloat(e && e.amount); return s + (isNaN(n) ? 0 : n);
  }, 0);
  var balance = amountDue - paid;

  var ms = useState(false); var modalOpen = ms[0]; var setModalOpen = ms[1];
  var es = useState(null);  var editEntry = es[0]; var setEditEntry = es[1]; // entry being edited

  function openModal() {
    setEditEntry(null);
    setModalOpen(true);
  }
  function openEdit(e) {
    setEditEntry(e);
    setModalOpen(true);
  }

  return (
    <div style={{ marginBottom: 14, padding: '12px 14px', background: '#F7F7F5', borderRadius: 4 }}>
      <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 8 }}>
        Payments
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4, color: '#555552' }}>
        <span>Asked</span><span>{fmtMoney(amountDue)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4, color: paid > 0 ? '#1A5C38' : '#555552' }}>
        <span>Paid</span><span>{fmtMoney(paid)}</span>
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 12, fontWeight: 500, paddingTop: 6,
        borderTop: '0.5px solid #D8D7D2',
        color: balance > 0.005 ? '#C8322A' : (balance < -0.005 ? '#3730A3' : '#1A5C38'),
      }}>
        <span>Balance due</span>
        <span>{balance < -0.005 ? '(' + fmtMoney(Math.abs(balance)) + ')' : fmtMoney(balance)}</span>
      </div>

      {props.canManage && props.onRecordPayment && (
        <button
          style={{
            marginTop: 10, width: '100%',
            padding: '6px 12px', border: '0.5px solid #111111', borderRadius: 4,
            background: '#FFFFFF', color: '#111111',
            fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
          }}
          onClick={openModal}>
          + Record Payment
        </button>
      )}

      {entries.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '0.5px solid #D8D7D2' }}>
          <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 6 }}>
            History
          </div>
          {entries.map(function(e, idx) {
            return (
              <div key={e.id || idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: 6,
                fontSize: 11, marginBottom: 6, paddingBottom: 6,
                borderBottom: idx < entries.length - 1 ? '0.5px dotted #D8D7D2' : 'none',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#111111', fontWeight: 500 }}>
                    {fmtMoney(e.amount)}{' '}
                    <span style={{ color: '#888880', fontWeight: 400 }}>· {(e.date || '').slice(0, 10)}</span>
                    {e.method && <span style={{ color: '#888880', fontWeight: 400 }}>{' '}· {e.method}</span>}
                  </div>
                  {e.reference && <div style={{ color: '#555552', marginTop: 1 }}>{e.reference}</div>}
                  {e.bankReference && <div style={{ color: '#888880', marginTop: 1, fontSize: 10 }}>Bank ref: {e.bankReference}</div>}
                  {e.notes && <div style={{ color: '#888880', marginTop: 1, fontSize: 10 }}>{e.notes}</div>}
                </div>
                {props.canManage && (
                  <div style={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {props.onUpdatePayment && (
                      <button
                        style={{ border: 'none', background: 'transparent', color: '#555', cursor: 'pointer', fontSize: 11, lineHeight: 1, padding: '2px 6px', textDecoration: 'underline', fontFamily: 'inherit' }}
                        title="Edit this payment"
                        onClick={function() { openEdit(e); }}>edit</button>
                    )}
                    <button
                      style={{ border: 'none', background: 'transparent', color: '#C8322A', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 4px' }}
                      title="Remove this payment entry"
                      onClick={function() {
                        if (window.confirm('Remove payment of ' + fmtMoney(e.amount) + '?')) {
                          if (props.onRemoveAllocation) props.onRemoveAllocation(e.id);
                        }
                      }}>×</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <RecordPaymentModal
          defaultAmount={editEntry ? String(editEntry.amount || '') : (balance > 0 ? balance.toFixed(2) : '')}
          initialEntry={editEntry}
          userEmail={props.userEmail || ''}
          onClose={function() { setModalOpen(false); setEditEntry(null); }}
          onSubmit={function(entry) {
            if (editEntry && props.onUpdatePayment) {
              // Edit path: same shape, but caller routes to updateClientPayment.
              props.onUpdatePayment(editEntry.paymentId, editEntry.id, {
                amount: entry.amount, date: entry.date, method: entry.method,
                reference: entry.reference, bankReference: entry.bankReference, notes: entry.notes,
              });
            } else {
              // New payment path: build payment + single allocation.
              var ctx = props.documentContext || {};
              var payment = {
                clientId:      ctx.clientId      || '',
                projectLabel:  ctx.projectLabel  || '',
                paymentDate:   entry.date,
                amountTotal:   entry.amount,
                method:        entry.method,
                bankReference: entry.bankReference,
                reference:     entry.reference,
                notes:         entry.notes,
                recordedBy:    entry.recordedBy,
              };
              var allocations = [{
                documentType: ctx.documentType || '',
                documentId:   ctx.documentId   || '',
                amount:       entry.amount,
              }];
              if (props.onRecordPayment) props.onRecordPayment(payment, allocations);
            }
            setModalOpen(false);
            setEditEntry(null);
          }} />
      )}
    </div>
  );
}

// ── RecordPaymentModal ────────────────────────────────────────────────────
//
// props:
//   defaultAmount  string — pre-fills the amount field
//   userEmail      string — stamps recordedBy on submit
//   onClose        ()      => void
//   onSubmit       (entry) => void   entry: { amount, date, method, reference, bankReference, notes, recordedBy }
function RecordPaymentModal(props) {
  // Edit mode pre-fills from initialEntry; new mode uses defaults.
  var ie = props.initialEntry || {};
  var as = useState(props.defaultAmount || (ie.amount != null ? String(ie.amount) : ''));
  var amount = as[0]; var setAmount = as[1];
  var ds = useState((ie.date || '').slice(0, 10) || new Date().toISOString().slice(0, 10));
  var date = ds[0]; var setDate = ds[1];
  var ms = useState(ie.method || '');
  var method = ms[0]; var setMethod = ms[1];
  var rs = useState(ie.reference || '');
  var reference = rs[0]; var setReference = rs[1];
  var brs = useState(ie.bankReference || '');
  var bankRef = brs[0]; var setBankRef = brs[1];
  var ns = useState(ie.notes || '');
  var notes = ns[0]; var setNotes = ns[1];
  var es = useState('');
  var err = es[0]; var setErr = es[1];
  var isEdit = !!props.initialEntry;

  function submit() {
    var amt = parseFloat(amount);
    if (!amt || amt <= 0) { setErr('Enter a positive amount.'); return; }
    if (!date) { setErr('Pick a date.'); return; }
    props.onSubmit({
      amount:        amt,
      date:          date,
      method:        method,
      reference:     reference.trim(),
      bankReference: bankRef.trim(),
      notes:         notes.trim(),
      recordedBy:    props.userEmail || '',
    });
  }

  var labelStyle = { fontSize: 9, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 4 };
  var inputStyle = { width: '100%', padding: '8px 10px', border: '0.5px solid #D8D7D2', borderRadius: 3, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 460, background: '#FFFFFF', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.3)', padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#111111', marginBottom: 16 }}>{isEdit ? 'Edit Payment' : 'Record Payment'}</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={labelStyle}>Amount</div>
            <input type="number" step="0.01" autoFocus value={amount}
              onChange={function(e) { setAmount(e.target.value); }}
              placeholder="0.00" style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>Date received</div>
            <input type="date" value={date}
              onChange={function(e) { setDate(e.target.value); }}
              style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>Payment method</div>
          <select value={method}
            onChange={function(e) { setMethod(e.target.value); }}
            style={Object.assign({}, inputStyle, { background: '#fff' })}>
            {PAYMENT_METHODS.map(function(m) {
              return <option key={m} value={m}>{m || '— Pick a method —'}</option>;
            })}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>Reference</div>
          <input type="text" value={reference}
            onChange={function(e) { setReference(e.target.value); }}
            placeholder="e.g. ABD transfer prior deposit / April hours wire"
            style={inputStyle} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={labelStyle}>Bank reference (optional)</div>
          <input type="text" value={bankRef}
            onChange={function(e) { setBankRef(e.target.value); }}
            placeholder="Wire confirmation # / check #"
            style={inputStyle} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={labelStyle}>Notes (optional)</div>
          <textarea rows={2} value={notes}
            onChange={function(e) { setNotes(e.target.value); }}
            style={Object.assign({}, inputStyle, { resize: 'vertical' })} />
        </div>

        {err && (
          <div style={{ marginBottom: 12, padding: '8px 10px', background: '#FDECEA', border: '0.5px solid #F2A39C', borderRadius: 3, fontSize: 11, color: '#C8322A' }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            style={{ padding: '6px 12px', border: '0.5px solid #D8D7D2', borderRadius: 4, background: '#FFFFFF', color: '#111111', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer' }}
            onClick={props.onClose}>
            Cancel
          </button>
          <button
            style={{ padding: '6px 12px', border: '0.5px solid #1A5C38', borderRadius: 4, background: '#1A5C38', color: '#FFFFFF', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer' }}
            onClick={submit}>
            {isEdit ? 'Save Changes' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
